/**
 * Meta Ads no Marketing (0162): a conta de anúncios da clínica e o painel.
 *
 * A conexão, a escolha da conta e a leitura passam pela borda `meta-ads`, que é
 * quem tem o token. Daqui só saem pedidos; o token nunca chega ao navegador.
 */

import { supabase } from '@/supabase';
import { atual, pode } from '@/session/sessao';
import type { PortaDeDados } from './porta';
import type {
    AppDaMetaDaPlataforma, CampanhaDeAnuncio, CargaDoPainelDeAnuncios, ContaDaMeta, ContaDeAnuncioDaClinica,
    LinhaDoPainelDeAnuncios,
} from './tipos';

const FUSO = 'America/Sao_Paulo';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501' || e.code === '22023') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

async function mensagemDaBorda(e: unknown): Promise<string | undefined> {
    const contexto = (e as { context?: unknown })?.context;
    if (contexto instanceof Response) {
        try {
            const corpo = await contexto.json();
            if (typeof corpo?.erro === 'string') return corpo.erro;
        } catch {
            /* Sem corpo legível: fica a mensagem genérica. */
        }
    }
    return undefined;
}

async function chamarMeta<T>(rota: string, corpo: Record<string, unknown>, falha: string): Promise<T> {
    const { data, error } = await cliente().functions.invoke(`meta-ads/${rota}`, { body: { clinica: clinicaId(), ...corpo } });
    if (error) throw new Error((await mensagemDaBorda(error)) ?? falha);
    return data as T;
}

/** `YYYY-MM-DD` em Brasília, `dias` para trás contando hoje. */
export function periodoDeDias(dias: number, agora: Date = new Date()): { de: string; ate: string } {
    const ate = agora.toLocaleDateString('en-CA', { timeZone: FUSO });
    const de = new Date(agora.getTime() - (dias - 1) * 86_400_000).toLocaleDateString('en-CA', { timeZone: FUSO });
    return { de, ate };
}

function paraConta(c: Record<string, unknown>): ContaDeAnuncioDaClinica {
    const texto = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
    const conta: ContaDeAnuncioDaClinica = {
        id: String(c.id),
        plataforma: 'meta',
        situacao: c.situacao as ContaDeAnuncioDaClinica['situacao'],
        conectadaEm: String(c.conectadaEm ?? ''),
    };
    const opcionais: Partial<ContaDeAnuncioDaClinica> = {
        contaExternaId: texto(c.contaExternaId), nome: texto(c.nome), moeda: texto(c.moeda),
        ultimoErro: texto(c.ultimoErro), usuarioExterno: texto(c.usuarioExterno), conectadaPor: texto(c.conectadaPor),
        tokenExpiraEm: texto(c.tokenExpiraEm), ultimaSincronizacaoEm: texto(c.ultimaSincronizacaoEm),
    };
    for (const [k, v] of Object.entries(opcionais)) if (v !== undefined) Object.assign(conta, { [k]: v });
    return conta;
}

function paraLinha(l: Record<string, unknown>): LinhaDoPainelDeAnuncios {
    const n = (v: unknown) => Number(v ?? 0) || 0;
    return {
        id: String(l.id),
        externoId: String(l.externoId ?? ''),
        nome: String(l.nome ?? ''),
        ...(typeof l.status === 'string' ? { status: l.status } : {}),
        ...(typeof l.objetivo === 'string' ? { objetivo: l.objetivo } : {}),
        ...(l.orcamentoDiarioCentavos != null ? { orcamentoDiarioCentavos: n(l.orcamentoDiarioCentavos) } : {}),
        investidoCentavos: n(l.investidoCentavos),
        impressoes: n(l.impressoes),
        cliques: n(l.cliques),
        leadsPlataforma: n(l.leadsPlataforma),
        conversasIniciadas: n(l.conversasIniciadas),
        leadsCrm: n(l.leadsCrm),
        ganhos: n(l.ganhos),
        receitaCentavos: n(l.receitaCentavos),
    };
}

export type MetodosDosAnuncios = Pick<PortaDeDados,
    'painelDeAnuncios' | 'contasDeAnuncio' | 'campanhasDeAnuncio' | 'conectarMeta' | 'contasDaMeta' | 'escolherContaDaMeta'
    | 'sincronizarMeta' | 'desconectarMeta' | 'appDaMeta' | 'salvarAppDaMeta' | 'removerAppDaMeta'>;

export const anunciosNoSupabase: MetodosDosAnuncios = {
    async contasDeAnuncio(): Promise<ContaDeAnuncioDaClinica[]> {
        const { data, error } = await cliente().rpc('contas_de_anuncio_da_clinica', { p_clinica: clinicaId() });
        erro('Falha ao ler as contas de anúncio', error);
        return ((data ?? []) as Record<string, unknown>[]).map(paraConta);
    },

    async painelDeAnuncios(dias: number): Promise<CargaDoPainelDeAnuncios> {
        const { de, ate } = periodoDeDias(dias);
        const [disponivelResp, contas, painelResp] = await Promise.all([
            cliente().rpc('meta_disponivel'),
            this.contasDeAnuncio(),
            cliente().rpc('painel_de_anuncios', { p_clinica: clinicaId(), p_de: de, p_ate: ate }),
        ]);
        erro('Falha ao ver se a Meta está disponível', disponivelResp.error);
        erro('Falha ao ler o painel de anúncios', painelResp.error);
        return {
            disponivel: Boolean(disponivelResp.data),
            conta: contas.find((c) => c.plataforma === 'meta') ?? null,
            de, ate, dias,
            linhas: ((painelResp.data ?? []) as Record<string, unknown>[]).map(paraLinha),
            podeGerir: pode('marketing') && !atual()?.impersonando,
        };
    },

    async campanhasDeAnuncio(): Promise<CampanhaDeAnuncio[]> {
        /* Sem permissão de marketing a RLS devolve vazio: o campo some do lead. */
        const { data, error } = await cliente().from('campanhas_de_anuncio').select('id, nome, status').order('nome');
        if (error) return [];
        return ((data ?? []) as Record<string, unknown>[]).map((c) => ({
            id: String(c.id), nome: String(c.nome ?? ''), ...(typeof c.status === 'string' ? { status: c.status } : {}),
        }));
    },

    async conectarMeta(destino: string): Promise<string> {
        const r = await chamarMeta<{ url?: string }>('conectar', { destino }, 'Não foi possível abrir o login da Meta.');
        if (!r?.url) throw new Error('A Meta não devolveu o endereço de login.');
        return r.url;
    },

    async contasDaMeta(): Promise<ContaDaMeta[]> {
        const r = await chamarMeta<{ contas?: ContaDaMeta[] }>('contas', {}, 'Não foi possível listar as contas da Meta.');
        return r?.contas ?? [];
    },

    async escolherContaDaMeta(contaId: string): Promise<void> {
        await chamarMeta('escolher', { conta: contaId }, 'Não foi possível usar esta conta.');
    },

    async sincronizarMeta(): Promise<{ campanhas: number; linhas: number }> {
        return chamarMeta('sincronizar', {}, 'Não foi possível ler os números da Meta.');
    },

    async desconectarMeta(): Promise<void> {
        const { error } = await cliente().rpc('desconectar_conta_de_anuncio', { p_clinica: clinicaId(), p_plataforma: 'meta' });
        erro('Falha ao desconectar a Meta', error);
    },

    async appDaMeta(): Promise<AppDaMetaDaPlataforma | null> {
        const { data, error } = await cliente().rpc('meta_app_da_plataforma');
        erro('Falha ao ler o app da Meta', error);
        const a = data as Record<string, unknown> | null;
        if (!a) return null;
        return {
            appId: String(a.appId),
            atualizadoEm: String(a.atualizadoEm ?? ''),
            ...(typeof a.dica === 'string' ? { dica: a.dica } : {}),
            ...(typeof a.configId === 'string' ? { configId: a.configId } : {}),
        };
    },

    async salvarAppDaMeta(appId: string, segredo: string, configId?: string): Promise<AppDaMetaDaPlataforma | null> {
        const { error } = await cliente().rpc('salvar_meta_app', {
            p_app_id: appId.trim(), p_segredo: segredo.trim(), p_config_id: configId?.trim() || null,
        });
        erro('Falha ao salvar o app da Meta', error);
        return this.appDaMeta();
    },

    async removerAppDaMeta(): Promise<void> {
        const { error } = await cliente().rpc('remover_meta_app');
        erro('Falha ao remover o app da Meta', error);
    },
};
